import { useEffect, useState } from "react";
import { configApi } from "@/lib/configApi";

// Promise a nivel de módulo (no de componente) -- si dos componentes de
// mapa montan useGoogleMaps() al mismo tiempo (ej. mismo formulario),
// ambos comparten la misma carga del script en vez de inyectarlo dos
// veces. Ver CLAUDE.md 2c.
let loaderPromise = null;

function loadGoogleMapsScript(apiKey) {
  if (window.google?.maps) return Promise.resolve();
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector("script[data-google-maps-loader]");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("No se pudo cargar Google Maps")));
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMapsLoader = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("No se pudo cargar Google Maps"));
    document.head.appendChild(script);
  }).catch((err) => {
    loaderPromise = null; // permitir reintentar en un próximo mount si falló
    throw err;
  });

  return loaderPromise;
}

/**
 * status:
 *  - "loading": todavía no se sabe si Maps está configurado
 *  - "ready": window.google.maps ya está disponible para usar
 *  - "unavailable": sin browser key configurada (o falló la carga) --
 *    las pantallas que dependen del mapa deben caer a su fallback de
 *    texto libre, nunca romper ni quedar en blanco (CLAUDE.md 2c)
 */
export function useGoogleMaps() {
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;

    configApi
      .getMapsBrowserKey()
      .then(({ data }) => {
        if (cancelled) return null;
        if (!data.configured || !data.api_key) {
          setStatus("unavailable");
          return null;
        }
        return loadGoogleMapsScript(data.api_key).then(() => {
          if (!cancelled) setStatus("ready");
        });
      })
      .catch(() => {
        if (!cancelled) setStatus("unavailable");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { status };
}

export default useGoogleMaps;
